import Seo from "@/components/Seo";

export default function About() {
  return (
    <main data-testid="about-page" className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <Seo
        title="About Nicola"
        description="Financial advisor, entrepreneur, and lifelong investment enthusiast. Moneyssance is my ordered journal on money and the fields around it."
        path="/about"
      />
      <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-6">
        About
      </div>
      <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.02] tracking-tighter">
        About me.
      </h1>

      <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Portrait — editorial silhouette placeholder */}
        <figure className="lg:col-span-4" data-testid="about-portrait">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-border bg-secondary">
            <PortraitSilhouette />
            <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] text-primary/70">
              N. B.
            </div>
            <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.3em] text-foreground/50">
              placeholder
            </div>
          </div>
          <figcaption className="mt-4 text-xs uppercase tracking-widest text-foreground/50">
            Portrait — a proper one, soon.
          </figcaption>
        </figure>

        {/* Copy */}
        <div className="lg:col-span-8">
          <div className="space-y-7 text-lg md:text-xl leading-[1.75] text-foreground/85 font-body">
            <p>
              I'm <span className="text-primary font-medium">Nicola</span> — financial
              advisor, entrepreneur, and lifelong investment enthusiast.
            </p>
            <p>
              Alongside years of personal studies in finance and economics, what truly
              drives me is genuine passion. I read constantly, think constantly, and
              keep refining how I understand money, decision-making, and long-term value.
            </p>
            <p>
              I created <em className="font-serif">Moneyssance</em> as my own{" "}
              <span className="text-primary">ordered journal</span> — a place to collect,
              organize, and give structure to the ideas I explore every day. It's where
              I track my thoughts, lessons, and evolving perspective on finance,
              investments, and the mindset, psychology behind them.
            </p>
            <p>
              Nothing more, nothing less: just a sincere space where I share the journey.
            </p>
          </div>

          <div className="mt-14 border-t border-border/60 pt-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50">
              Say hello
            </div>
            <div className="font-serif text-2xl mt-2">
              Reach me via the newsletter form below, or on{" "}
              <a
                href="mailto:hello@moneyssance.com"
                className="text-primary underline underline-offset-4 hover:opacity-80 transition-opacity"
                data-testid="about-email"
              >
                hello@moneyssance.com
              </a>
              .
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function PortraitSilhouette() {
  // Editorial B&W silhouette rendered inline for full palette control.
  // Uses the site's primary + secondary CSS variables so it always matches the theme.
  return (
    <svg
      viewBox="0 0 400 500"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 w-full h-full"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="paperBg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--secondary))" />
          <stop offset="100%" stopColor="hsl(var(--muted))" />
        </linearGradient>
        <linearGradient id="figureFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.85)" />
        </linearGradient>
        <filter id="grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.1
                    0 0 0 0 0.1
                    0 0 0 0 0.1
                    0 0 0 0.07 0"
          />
        </filter>
      </defs>

      {/* Warm paper background */}
      <rect width="400" height="500" fill="url(#paperBg)" />

      {/* Thin accent horizon line */}
      <line
        x1="0"
        y1="360"
        x2="400"
        y2="360"
        stroke="hsl(var(--accent))"
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />

      {/* Head-and-shoulders silhouette, gently stylized, left profile */}
      <path
        d="
          M 120 500
          L 120 430
          C 120 400, 145 388, 175 380
          C 205 372, 220 362, 220 340
          C 220 335, 218 330, 216 326
          C 200 310, 188 292, 184 274
          C 176 236, 178 205, 200 185
          C 220 168, 250 160, 270 175
          C 290 190, 296 216, 292 246
          C 288 274, 276 292, 262 308
          C 258 314, 256 320, 258 328
          C 262 344, 274 356, 296 366
          C 322 378, 340 390, 348 410
          C 354 425, 356 448, 356 500
          Z
        "
        fill="url(#figureFill)"
      />

      {/* Subtle collar edge highlight */}
      <path
        d="M 130 480 Q 240 468 340 486"
        fill="none"
        stroke="hsl(var(--background))"
        strokeOpacity="0.12"
        strokeWidth="1"
      />

      {/* Grain overlay */}
      <rect width="400" height="500" filter="url(#grain)" opacity="0.6" />
    </svg>
  );
}
